import { Dispatch, State, Thunk } from '../../client/configureStore';
import {
  SettingsViewContent,
  SettingsViewState,
  SettingsViewTab,
} from '../../client/appTypings';
import fetch from 'isomorphic-fetch';
import { createMarkup } from '../../client/SettingsView/createMarkup';
import { getTranslation } from '../../client/localizations/translations';
import { generateDeviceInfoHtml } from '../../client/SettingsView/deviceInfoHtml';
import { getInfoEntries } from '../../client/hooks/useInformation';
import { generateDataProtectionHtml } from '../../client/SettingsView/dataProtectionHtml';
import { contactPageHtml } from '../../client/SettingsView/contactPageHtml';
import {
  replaceSettingsViewDataAction,
  updateSettingsViewDataAction,
} from './actions';
import { Language } from '@yleisradio/areena-types';
import { LoggedInUser } from '../../common/types';
import { getUILanguage } from '../../client/localizations/language';
import { generateAccessibilityStatementHtml } from '../../client/SettingsView/accessibilityStatementHtml';

const instructionUrls = {
  fi: 'https://static.cdn.yle.fi/10m/mobile-config/help/areena-lg/index_fi.html',
  sv: 'https://static.cdn.yle.fi/10m/mobile-config/help/areena-lg/index_sv.html',
};

const getSettigsViewData = (
  languageCode: Language,
  isSubAccount = false,
  infoEntries: [string, string][]
): SettingsViewState => {
  return [
    {
      title: getTranslation('common_start_screen_TV', languageCode),
      type: 'start-view',
    },
    {
      title: getTranslation('settingsView_language', languageCode),
      type: 'language-change',
    },
    {
      title: getTranslation('settingsView_instructions', languageCode),
      type: 'instructions',
    },
    {
      htmlDocument: generateDeviceInfoHtml(languageCode, infoEntries),
      title: getTranslation('settingsView_device', languageCode),
      type: 'device-info',
    },
    ...(!isSubAccount
      ? ([
          {
            htmlDocument: generateDataProtectionHtml(
              languageCode,
              getTranslation('settingsView_dataProtection', languageCode)
            ),
            title: getTranslation('settingsView_dataProtection', languageCode),
            type: 'data-protection',
          },
          {
            htmlDocument: generateAccessibilityStatementHtml(
              languageCode,
              getTranslation(
                'settingsView_accessibilityStatement',
                languageCode
              )
            ),
            title: getTranslation(
              'settingsView_accessibilityStatement',
              languageCode
            ),
            type: 'accessibility-statement',
          },
          {
            title: getTranslation('settingsView_cookieSettings', languageCode),
            type: 'consent-settings',
          },
        ] as SettingsViewContent[])
      : []),
    {
      htmlDocument: contactPageHtml[languageCode],
      title: getTranslation('settingsView_contactInfo', languageCode),
      type: 'contact-page',
    },
    {
      title: getTranslation('logoutPopup_logout', languageCode),
      type: 'logout',
    },
    {
      title: getTranslation('settingsView_closeApp', languageCode),
      type: 'close-app',
    },
  ];
};

const getHtmlContentThunk =
  (
    tabIndex: number,
    htmlUri: string,
    type: SettingsViewTab
  ): Thunk<Promise<void>> =>
  async (dispatch: Dispatch) => {
    const response = await fetch(htmlUri);
    if (response.status === 200) {
      const htmlDocument = await response.text();

      dispatch(
        updateSettingsViewDataAction({
          tabIndex,
          htmlDocument: createMarkup(htmlDocument),
          type,
        })
      );
    }
  };

export const getSettingsViewThunk =
  (user: LoggedInUser | null): Thunk<Promise<void>> =>
  async (
    dispatch: Dispatch,
    getState: () => State,
    { appFeatures: { device, version } }
  ) => {
    const { currentAddress, settingsView } = getState();

    const infoEntries = getInfoEntries(device, currentAddress, version);

    if (settingsView.length === 0) {
      const languageCode = getUILanguage();
      const settingsViewData: SettingsViewState = getSettigsViewData(
        languageCode,
        user?.isSubAccount,
        infoEntries
      );

      dispatch(replaceSettingsViewDataAction({ data: settingsViewData }));

      await dispatch(
        getHtmlContentThunk(2, instructionUrls[languageCode], 'instructions')
      );
    }
  };
